import { useEffect, useMemo, useState } from "react";
import type { Bible } from "../domain/types";
import { getMoment, momentsForChapter, adjacentMoment } from "../domain/engine";
import { useStore } from "../store";

export function TimeLens({ bible }: { bible: Bible }) {
  const { selection, setSelection } = useStore();
  const moment = getMoment(bible, selection.momentId);
  const bookId = selection.bookId ?? moment?.bookId ?? null;
  const [chapterDraft, setChapterDraft] = useState<number | null>(null);
  const chapter = moment?.chapterNumber ?? chapterDraft;
  useEffect(() => {
    if (!bookId) setChapterDraft(null);
  }, [bookId]);
  const chapters = useMemo(() => {
    if (!bookId) return [];
    const nums = [...new Set(bible.moments.filter((m) => m.bookId === bookId).map((m) => m.chapterNumber))];
    return nums.sort((a, b) => a - b);
  }, [bible.moments, bookId]);
  const options = bookId != null && chapter != null ? momentsForChapter(bible, bookId, chapter) : [];
  const ambiguous = options.length > 1 && !selection.momentId;

  return (
    <div className="lens" aria-label="Lentille temporelle">
      <div className="lens-ring">
        <div className="orb" aria-hidden="true" />
        <div>
          <div className="display" style={{ fontSize: "1.15rem" }}>
            {moment ? moment.label : bookId ? `${bible.books.find((b) => b.id === bookId)?.title} · vue de tome` : "Vue générale — aucun filtre temporel"}
          </div>
          <div className="tiny">
            {moment
              ? `Lecture ${moment.readingOrder} · interne ${moment.storyOrder ?? "?"} · ${bible.branches.find((b) => b.id === moment.branchId)?.name ?? moment.branchId}${moment.loopIterationId ? ` · ${moment.loopIterationId}` : ""}`
              : "Trajectoires complètes, spoilers auteur autorisés. Aucun état n’est déclaré « actuel »."}
          </div>
        </div>
      </div>
      <div className="lens-controls">
        <button className={`btn ${!bookId && !moment ? "primary" : ""}`} type="button" onClick={() => { setChapterDraft(null); setSelection({ bookId: null, momentId: null }); }}>
          Vue générale
        </button>
        <label className="field" style={{ minWidth: 140 }}>
          <span>Tome</span>
          <select
            value={bookId ?? ""}
            onChange={(e) => {
              const v = e.target.value || null;
              setChapterDraft(null);
              setSelection({ bookId: v, momentId: null });
            }}
          >
            <option value="">Tous</option>
            {bible.books.map((b) => (
              <option key={b.id} value={b.id}>
                {b.number}. {b.title}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ minWidth: 120 }}>
          <span>Chapitre</span>
          <select
            value={chapter ?? ""}
            disabled={!bookId}
            onChange={(e) => {
              const n = e.target.value === "" ? null : Number(e.target.value);
              if (n == null || !bookId) {
                setChapterDraft(null);
                setSelection({ momentId: null, bookId });
                return;
              }
              setChapterDraft(n);
              const list = momentsForChapter(bible, bookId, n);
              if (list.length === 1) setSelection({ bookId, momentId: list[0]!.id });
              else setSelection({ bookId, momentId: null });
            }}
          >
            <option value="">Tous</option>
            {chapters.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        {options.length > 1 && (
          <label className="field" style={{ minWidth: 220 }}>
            <span>Branche / itération</span>
            <select
              value={selection.momentId ?? ""}
              onChange={(e) => setSelection({ momentId: e.target.value || null, bookId })}
            >
              <option value="">Choisir explicitement…</option>
              {options.map((m) => (
                <option key={m.id} value={m.id}>
                  {bible.branches.find((b) => b.id === m.branchId)?.name} {m.loopIterationId ? `· ${m.loopIterationId}` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field" style={{ minWidth: 140 }}>
          <span>Ordre</span>
          <select
            value={selection.orderMode}
            onChange={(e) => setSelection({ orderMode: e.target.value as "reading" | "story" })}
          >
            <option value="reading">Lecture</option>
            <option value="story">Chronologie interne</option>
          </select>
        </label>
        <label className="field" style={{ minWidth: 140 }}>
          <span>Regard</span>
          <select
            value={selection.viewer}
            onChange={(e) => setSelection({ viewer: e.target.value as "author" | "reader" })}
          >
            <option value="author">Auteur</option>
            <option value="reader">Lecteur (filtré)</option>
          </select>
        </label>
        <button
          className="btn"
          type="button"
          disabled={!moment}
          onClick={() => {
            if (!moment) return;
            const prev = adjacentMoment(bible, moment.id, -1, selection.orderMode);
            if (prev) setSelection({ momentId: prev.id, bookId: prev.bookId });
          }}
        >
          ←
        </button>
        <button
          className="btn"
          type="button"
          disabled={!moment}
          onClick={() => {
            if (!moment) return;
            const next = adjacentMoment(bible, moment.id, 1, selection.orderMode);
            if (next) setSelection({ momentId: next.id, bookId: next.bookId });
          }}
        >
          →
        </button>
      </div>
      {ambiguous && (
        <p className="warn tiny">
          Plusieurs branches existent pour ce chapitre. Choisissez-en une : le système ne fusionne jamais les itérations.
        </p>
      )}
    </div>
  );
}
